import ApiService from "api/ApiService";
import { loadStripe, Stripe } from "@stripe/stripe-js";

export enum PaymentStatus {
  SUCCESS = "success",
  FAILED = "failed",
  REFUNDED = "refunded",
}

export interface PaymentHistory {
  id: number;
  amount: number;
  currency: string;
  credits_purchased: number;
  created_timestamp: string;
  status: string;
}

export interface CheckoutSession {
  session_id: string;
  url: string;
}

export interface CreditBalance {
  credit_balance: number;
  total_credits_purchased: number;
}

export default class PaymentService {
  private static getBaseUrl(): string {
    return "api";
  }

  private static async getStripe(): Promise<Stripe | null> {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (!stripeKey) {
      throw new Error("Stripe public key is not defined");
    }
    // Force HTTPS in production
    if (
      process.env.NODE_ENV === "production" &&
      window.location.protocol !== "https:"
    ) {
      throw new Error("Stripe requires HTTPS in production");
    }
    return await loadStripe(stripeKey);
  }

  public static async checkStripeConnection(): Promise<{
    isConnected: boolean;
  }> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getBaseUrl()}/stripe/status`,
          method: "GET",
        },
        true,
      );
      if (typeof response?.isConnected !== "boolean") {
        throw new Error("Invalid response format");
      }
      return response;
    } catch (error) {
      console.error("Check stripe connection error:", error);
      return Promise.reject(error);
    }
  }

  public static async connectStripe(): Promise<void> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getBaseUrl()}/stripe/connect`,
          method: "POST",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async disconnectStripe(): Promise<void> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getBaseUrl()}/stripe/connect`,
          method: "DELETE",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async getCreditBalance(): Promise<CreditBalance> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getBaseUrl()}/credits/balance`,
          method: "GET",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async getPaymentHistory(): Promise<{
    history: PaymentHistory[];
  }> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getBaseUrl()}/credits/history`,
          method: "GET",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async purchaseCredits(quantity: number): Promise<void> {
    try {
      // Get checkout session URL from backend
      const response = await ApiService.request(
        {
          url: `${this.getBaseUrl()}/credits/purchase`,
          method: "POST",
          data: { amount: quantity },
        },
        true,
      );

      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error) {
      console.error("Purchase credits error:", error);
      throw error;
    }
  }

  public static async verifyPaymentSession(
    sessionId: string,
  ): Promise<boolean> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getBaseUrl()}/credits/verify-session`,
          method: "POST",
          data: { session_id: sessionId },
        },
        true,
      );
      return response.success;
    } catch (error) {
      console.error("Payment verification error:", error);
      throw error;
    }
  }
}
